import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowUp, ArrowDown, X, Plus, Upload, Undo2, Trash2 } from "lucide-react";
import { SECTION_DEFAULTS } from "@/data/defaultPhotoLayouts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbs = (url) => (url && url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

// The full list of overridable photo sections + a human-friendly label.
// The DEFAULTS are only used to give admins a visual reference on first load;
// the real "current" list comes from server override OR falls back to defaults
// baked into Story.jsx / Home.jsx.
const SECTIONS = [
  { key: "home.hero", label: "Home — Hero (background)", single: true },
  { key: "home.storyPreview", label: "Home — Our Story preview", single: true },
  { key: "home.travelPreview", label: "Home — Travel preview", single: true },
  ...Array.from({ length: 13 }, (_, i) => ({
    key: `story.beats.${i}`,
    label: `Story — Chapter ${["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII"][i]}`,
  })),
  { key: "story.venue", label: "Story — Wedding venue trio" },
];

const AdminPhotoLayout = () => {
  const [layouts, setLayouts] = useState({}); // { section: [urls] } — server truth
  const [drafts, setDrafts] = useState({});   // { section: [urls] } — pending edits
  const [library, setLibrary] = useState([]); // uploaded photos to pick from
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, pRes] = await Promise.all([
        api.get("/photo-layouts"),
        // Admin needs to see EVERY photo (guest + admin uploads) for the picker
        api.get("/admin/photos"),
      ]);
      const map = {};
      for (const l of lRes.data.layouts || []) map[l.section] = l.photos || [];
      setLayouts(map);
      setDrafts({});
      setLibrary(pRes.data || []);
    } catch (_e) {
      toast.error("Failed to load photo layouts.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const currentPhotos = (section) =>
    drafts[section] !== undefined
      ? drafts[section]
      : (layouts[section] ?? SECTION_DEFAULTS[section] ?? null);

  const isDirty = (section) =>
    drafts[section] !== undefined &&
    JSON.stringify(drafts[section]) !== JSON.stringify(layouts[section] ?? null);

  // "Are we showing built-in defaults (no override saved)?"
  const isShowingDefaults = (section) =>
    !(section in layouts) && drafts[section] === undefined;

  const setSection = (section, photos) =>
    setDrafts((d) => ({ ...d, [section]: photos }));

  const move = (section, idx, delta) => {
    const arr = [...(currentPhotos(section) || [])];
    const j = idx + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSection(section, arr);
  };
  const removeAt = (section, idx) => {
    const arr = [...(currentPhotos(section) || [])];
    arr.splice(idx, 1);
    setSection(section, arr);
  };
  const addUrl = (section, url) => {
    if (!url) return;
    const arr = [...(currentPhotos(section) || []), url];
    setSection(section, arr);
  };

  const uploadNew = async (section, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Tag admin-CMS uploads so they DON'T appear in the public guest gallery.
      fd.append("source", "admin");
      const { data } = await api.post("/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const abs = toAbs(data.url);
      setLibrary((lib) => [data, ...lib]);
      addUrl(section, abs);
      toast.success("Uploaded — added to section (unsaved).");
    } catch (_e) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  /**
   * Permanently delete a photo from the library.
   * Also strips it from every section layout so it stops appearing on the site.
   *
   * For DEFAULT photos (external URLs not in the library), we cannot literally
   * delete the underlying file — those live in the Emergent asset bucket and
   * are the built-in defaults. In that case we save an override for JUST this
   * section that omits the photo, which is what "deleting" a default means to
   * the couple in practice.
   */
  const deleteFromLibrary = useCallback(async (photoUrl, section) => {
    const abs = photoUrl;
    const match = library.find(
      (p) => toAbs(p.url) === abs || p.url === abs,
    );

    if (!match) {
      // Not in the library (i.e. a built-in default). Offer to remove it from
      // JUST this section — the file cannot be deleted from disk.
      if (
        !window.confirm(
          "This is a built-in default photo — it can't be deleted from storage.\n\nRemove it from this section only?",
        )
      ) {
        return;
      }
      const arr = [...(currentPhotos(section) || [])].filter((u) => u !== abs);
      setSection(section, arr);
      toast.success("Removed from this section. Hit Save to make it live.");
      return;
    }

    if (
      !window.confirm(
        "Permanently delete this photo from the library?\n\nIt will also be removed from every section that uses it. This cannot be undone.",
      )
    ) {
      return;
    }
    try {
      await api.delete(`/photos/${match.id}`);
      // Strip from every layout (draft + saved)
      setDrafts((d) => {
        const next = { ...d };
        for (const k of Object.keys(next)) {
          next[k] = (next[k] || []).filter((u) => u !== abs);
        }
        return next;
      });
      setLayouts((l) => {
        const next = { ...l };
        for (const k of Object.keys(next)) {
          next[k] = (next[k] || []).filter((u) => u !== abs);
        }
        return next;
      });
      setLibrary((lib) => lib.filter((p) => p.id !== match.id));
      toast.success("Photo deleted from library and all sections.");
    } catch (_e) {
      toast.error("Delete failed.");
    }
  }, [library, currentPhotos]);

  const save = async (section) => {
    setSavingSection(section);
    try {
      const photos = currentPhotos(section) || [];
      await api.post("/admin/photo-layouts", { section, photos });
      setLayouts((l) => ({ ...l, [section]: photos }));
      setDrafts((d) => { const n = { ...d }; delete n[section]; return n; });
      toast.success("Saved. Live now.");
    } catch (_e) {
      toast.error("Save failed.");
    } finally {
      setSavingSection(null);
    }
  };

  const revert = async (section) => {
    if (!window.confirm("Reset this section to the hardcoded default?")) return;
    setSavingSection(section);
    try {
      await api.delete(`/admin/photo-layouts/${encodeURIComponent(section)}`);
      setLayouts((l) => { const n = { ...l }; delete n[section]; return n; });
      setDrafts((d) => { const n = { ...d }; delete n[section]; return n; });
      toast.success("Reset to default.");
    } catch (_e) {
      toast.error("Reset failed.");
    } finally {
      setSavingSection(null);
    }
  };

  const libraryUrls = useMemo(() => library.map((p) => ({ url: toAbs(p.url), name: p.original_name || p.filename })), [library]);

  return (
    <div data-testid="admin-photo-layout" className="space-y-8">
      <div>
        <p className="editorial-kicker">Photo placement editor</p>
        <p className="text-sm text-foreground/60 mt-1">
          Every section previews its <b>current photos</b> — including built-in defaults.
          Reorder, remove from a section, or delete permanently — controls sit on every photo.
          Deleting a default just removes it from that section (defaults live in code and can't be nuked from storage).
          Use the buttons below each section to add photos (upload new, pick from library, or paste a URL).
          Reset returns to the built-in default.
        </p>
      </div>

      {loading ? (
        <p className="editorial-kicker py-8 text-center text-foreground/50">Loading…</p>
      ) : (
        <div className="space-y-6">
          {SECTIONS.map(({ key, label, single }) => {
            const photos = currentPhotos(key) || [];
            const hasOverride = key in layouts;
            const dirty = isDirty(key);
            const busy = savingSection === key;
            return (
              <section
                key={key}
                data-testid={`layout-section-${key}`}
                className={`surface-card p-5 ${dirty ? "ring-1 ring-accent/50" : ""}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <h3 className="font-serif-display text-xl">{label}</h3>
                    <code className="text-[10px] text-foreground/50 font-mono">{key}</code>
                    {hasOverride && !dirty && <span className="ml-3 text-[10px] uppercase tracking-widest text-accent">overridden</span>}
                    {isShowingDefaults(key) && photos.length > 0 && (
                      <span className="ml-3 text-[10px] uppercase tracking-widest text-foreground/45">
                        showing defaults
                      </span>
                    )}
                    {dirty && <span className="ml-3 text-[10px] uppercase tracking-widest text-accent">unsaved</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {hasOverride && (
                      <button
                        type="button"
                        onClick={() => revert(key)}
                        disabled={busy}
                        data-testid={`layout-revert-${key}`}
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-foreground/60 hover:text-accent"
                      >
                        <Undo2 className="w-3 h-3" /> Reset to default
                      </button>
                    )}
                    <Button
                      onClick={() => save(key)}
                      disabled={!dirty || busy}
                      data-testid={`layout-save-${key}`}
                      className="h-9 px-4 bg-foreground text-background hover:bg-foreground/90 text-[11px] tracking-[0.18em] uppercase"
                    >
                      {busy ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>

                {photos.length === 0 ? (
                  <div className="border border-dashed border-border rounded-md p-6 text-center editorial-kicker text-foreground/50">
                    {hasOverride
                      ? "Overridden empty — nothing shows on the site."
                      : "No defaults for this section."}
                  </div>
                ) : (
                  <>
                  <p
                    className="text-[10px] tracking-[0.18em] uppercase text-foreground/45 mb-2"
                    data-testid={`layout-legend-${key}`}
                  >
                    <span className="inline-flex items-center gap-1 mr-4"><ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> Reorder</span>
                    <span className="inline-flex items-center gap-1 mr-4"><X className="w-3 h-3" /> Remove from this section</span>
                    <span className="inline-flex items-center gap-1 text-destructive/80"><Trash2 className="w-3 h-3" /> Delete permanently</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                    {photos.map((url, i) => (
                      <div
                        key={`${url}-${i}`}
                        className="relative surface-card overflow-hidden"
                        data-testid={`layout-photo-${key}-${i}`}
                      >
                        <img
                          src={url}
                          alt=""
                          loading="lazy"
                          className="block w-full aspect-square object-cover"
                          onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                        />
                        {/* Position badge */}
                        <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-1.5 rounded pointer-events-none">
                          {i + 1}
                        </div>
                        {/* Always-visible control bar (below the image) */}
                        <div className="flex items-center justify-between gap-1 px-2 py-1.5 border-t border-border bg-background/70">
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => move(key, i, -1)}
                              disabled={i === 0}
                              className="w-7 h-7 rounded border border-border text-foreground/70 hover:text-foreground hover:border-foreground/60 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                              title="Move earlier"
                              aria-label="Move earlier"
                              data-testid={`layout-up-${key}-${i}`}
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => move(key, i, 1)}
                              disabled={i === photos.length - 1}
                              className="w-7 h-7 rounded border border-border text-foreground/70 hover:text-foreground hover:border-foreground/60 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                              title="Move later"
                              aria-label="Move later"
                              data-testid={`layout-down-${key}-${i}`}
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => removeAt(key, i)}
                              className="w-7 h-7 rounded border border-border text-foreground/70 hover:text-foreground hover:border-foreground/60 flex items-center justify-center transition-colors"
                              title="Remove from this section only"
                              aria-label="Remove from section"
                              data-testid={`layout-remove-${key}-${i}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteFromLibrary(url, key)}
                              className="w-7 h-7 rounded border border-destructive/40 text-destructive/80 hover:text-destructive hover:border-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors"
                              title="Delete photo permanently from the library (defaults are removed from section only)"
                              aria-label="Delete permanently"
                              data-testid={`layout-delete-${key}-${i}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )}

                {/* Adders */}
                {(!single || photos.length === 0) && (
                  <div className="mt-4 border-t border-border pt-4 grid gap-3 sm:grid-cols-3">
                    <label className="inline-flex items-center justify-center gap-2 h-10 border border-border rounded-md cursor-pointer hover:bg-foreground/[0.03] text-xs">
                      <Upload className="w-4 h-4" />
                      {uploading ? "Uploading…" : "Upload new photo"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading}
                        data-testid={`layout-upload-${key}`}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadNew(key, f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <select
                      className="h-10 px-3 border border-border rounded-md bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      data-testid={`layout-picker-${key}`}
                      value=""
                      onChange={(e) => { addUrl(key, e.target.value); e.target.value = ""; }}
                    >
                      <option value="">Pick from library ({libraryUrls.length})…</option>
                      {libraryUrls.map((l, i) => (
                        <option key={i} value={l.url}>{l.name || l.url.slice(-24)}</option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Paste image URL"
                        data-testid={`layout-url-${key}`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            addUrl(key, e.currentTarget.value.trim());
                            e.currentTarget.value = "";
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {single && photos.length > 0 && (
                  <p className="mt-3 text-[10px] uppercase tracking-widest text-foreground/50">This section shows only the first image.</p>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPhotoLayout;
